import React, { useCallback, useState, createContext, ReactNode } from "react";
import { Snackbar, Alert } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import WbSunnyIcon from "@mui/icons-material/WbSunny";

interface Toast {
    id: number;
    mostrar: boolean;
    titulo: string;
    tipo: "Success" | "Danger" | "Warning" | "Info" | "Dark" | "Light";
    mensaje: string;
}

interface ToastContextProps {
    addToast: (toast: Omit<Toast, "id">) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export default ToastContext;

let id = 0;

interface ToastContextProviderProps {
    children: ReactNode;
}

export const ToastContextProvider: React.FC<ToastContextProviderProps> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const addToast = useCallback((toast: Omit<Toast, "id">) => {
        setToasts((prevToasts) => [...prevToasts, { id: id++, ...toast }]);
    }, []);

    const handleClose = (id: number) => {
        setToasts((prevToasts) => prevToasts.filter((t) => t.id !== id));
    };

    const getSeverity = (tipo: Toast["tipo"]) => {
        switch (tipo) {
            case "Success":
                return "success";
            case "Danger":
                return "error";
            case "Warning":
                return "warning";
            case "Info":
                return "info";
            default:
                return "info"; // Para "Dark" y "Light"
        }
    };

    const getIcon = (tipo: Toast["tipo"]) => {
        switch (tipo) {
            case "Success":
                return <CheckCircleIcon />;
            case "Danger":
                return <ErrorIcon />;
            case "Warning":
                return <WarningIcon />;
            case "Info":
                return <InfoIcon />;
            case "Dark":
                return <DarkModeIcon />;
            case "Light":
                return <WbSunnyIcon />;
            default:
                return <InfoIcon />;
        }
    };

    return (
        <ToastContext.Provider value={{ addToast }}>
            {children}
            {toasts.map((toast) => (
                <Snackbar
                    key={toast.id}
                    open={toast.mostrar}
                    autoHideDuration={4000}
                    onClose={() => handleClose(toast.id)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                >
                    <Alert
                        onClose={() => handleClose(toast.id)}
                        severity={getSeverity(toast.tipo)}
                        variant="filled"
                        icon={getIcon(toast.tipo)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "pre-line", 
                            maxWidth: "400px", 
                            wordBreak: "break-word", 
                        }}
                    >
                        {toast.mensaje}
                    </Alert>
                </Snackbar>
            ))}
        </ToastContext.Provider>
    );
};
